import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserWATemplates = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [builderData, setBuilderData] = useState({
    name: '', category: 'MARKETING', language: 'en', headerType: 'none', headerText: '',
    bodyText: '', footerText: '', buttons: [],
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/templates?status=${filterStatus}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const statusColors = {
    APPROVED: { bg: '#dcfce7', text: '#166534' },
    PENDING: { bg: '#fef3c7', text: '#92400e' },
    REJECTED: { bg: '#fee2e2', text: '#991b1b' },
    PAUSED: { bg: '#f3e8ff', text: '#6b21a8' },
    DISABLED: { bg: '#f3f4f6', text: '#374151' },
  };

  const categoryColors = {
    MARKETING: { bg: '#dcfce7', text: '#166534' },
    UTILITY: { bg: '#dbeafe', text: '#1e40af' },
    AUTHENTICATION: { bg: '#f3e8ff', text: '#6b21a8' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.FileText className="w-5 h-5 text-white" />
            </div>
            Template Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Create, manage, and monitor WhatsApp message templates</p>
        </div>
        <button onClick={() => setActiveTab('builder')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
          <Icons.Plus className="w-4 h-4" /> Create Template
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Templates', value: templates.length || '0', icon: 'FileText', color: '#25D366' },
          { label: 'Approved', value: templates.filter(t => t.status === 'APPROVED').length || '0', icon: 'CheckCircle', color: '#128C7E' },
          { label: 'Pending', value: templates.filter(t => t.status === 'PENDING').length || '0', icon: 'Clock', color: '#F59E0B' },
          { label: 'Rejected', value: templates.filter(t => t.status === 'REJECTED').length || '0', icon: 'XCircle', color: '#ef4444' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 18)}
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-[11px] text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { key: 'templates', label: 'All Templates', icon: 'List' },
            { key: 'builder', label: 'Template Builder', icon: 'Wand2' },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Template List */}
          {activeTab === 'templates' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {['all', 'APPROVED', 'PENDING', 'REJECTED'].map((status) => (
                  <button key={status} onClick={() => setFilterStatus(status)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: filterStatus === status ? 'rgba(37, 211, 102, 0.1)' : 'transparent',
                      color: filterStatus === status ? '#128C7E' : '#6b7280',
                      border: filterStatus === status ? '1px solid rgba(37, 211, 102, 0.2)' : '1px solid transparent',
                    }}
                  >
                    {status === 'all' ? 'All' : status}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-gray-500">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                    <Icons.FileText className="w-8 h-8" style={{ color: '#25D366' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No Templates Yet</h3>
                  <p className="text-sm text-gray-500 mb-4">Create your first WhatsApp message template to start sending campaigns.</p>
                  <button onClick={() => setActiveTab('builder')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                    <Icons.Plus className="w-4 h-4" /> Create Template
                  </button>
                </div>
              ) : (
                templates.map((tpl) => (
                  <div key={tpl.id} className="p-4 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                          <Icons.FileText className="w-5 h-5" style={{ color: '#25D366' }} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{tpl.name}</h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-medium" style={{
                              background: categoryColors[tpl.category]?.bg || '#f3f4f6',
                              color: categoryColors[tpl.category]?.text || '#374151',
                            }}>{tpl.category}</span>
                            <span className="text-[10px] text-gray-400">Lang: {tpl.language}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                          background: statusColors[tpl.status]?.bg || '#f3f4f6',
                          color: statusColors[tpl.status]?.text || '#374151',
                        }}>{tpl.status}</span>
                        {tpl.quality_score && tpl.quality_score !== 'UNKNOWN' && (
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${tpl.quality_score === 'GREEN' ? 'bg-green-50 text-green-700' : tpl.quality_score === 'YELLOW' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                            Quality: {tpl.quality_score}
                          </span>
                        )}
                      </div>
                    </div>
                    {(tpl.total_sent || 0) > 0 && (
                      <div className="flex items-center gap-6 text-xs text-gray-500 mt-2 pt-2 border-t border-gray-50">
                        <span>Sent: <strong className="text-gray-700">{(tpl.total_sent || 0).toLocaleString()}</strong></span>
                        <span>Delivered: <strong className="text-green-600">{(tpl.total_delivered || 0).toLocaleString()}</strong></span>
                        <span>Read: <strong className="text-blue-600">{(tpl.total_read || 0).toLocaleString()}</strong></span>
                        {tpl.last_used_at && <span className="ml-auto text-gray-400">Last used: {tpl.last_used_at}</span>}
                        <div className="flex items-center gap-1">
                          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"><Icons.Eye className="w-3.5 h-3.5" /></button>
                          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"><Icons.Pencil className="w-3.5 h-3.5" /></button>
                          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"><Icons.Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Template Builder */}
          {activeTab === 'builder' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-5">
                <h3 className="font-semibold text-gray-900">Template Configuration</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Name</label>
                  <input type="text" value={builderData.name} onChange={(e) => setBuilderData({ ...builderData, name: e.target.value })}
                    placeholder="e.g. order_update_v2" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                  <p className="text-[10px] text-gray-400 mt-1">Lowercase, underscores only. Max 512 characters.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                    <select value={builderData.category} onChange={(e) => setBuilderData({ ...builderData, category: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400">
                      <option>MARKETING</option>
                      <option>UTILITY</option>
                      <option>AUTHENTICATION</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Language</label>
                    <select value={builderData.language} onChange={(e) => setBuilderData({ ...builderData, language: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400">
                      <option value="en">English (en)</option>
                      <option value="hi">Hindi (hi)</option>
                      <option value="gu">Gujarati (gu)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Header (Optional)</label>
                  <div className="flex gap-2 mb-2">
                    {['none', 'text', 'image', 'video', 'document'].map((type) => (
                      <button key={type} onClick={() => setBuilderData({ ...builderData, headerType: type })}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize"
                        style={{
                          borderColor: builderData.headerType === type ? '#25D366' : '#e5e7eb',
                          background: builderData.headerType === type ? 'rgba(37, 211, 102, 0.08)' : 'white',
                          color: builderData.headerType === type ? '#128C7E' : '#6b7280',
                        }}
                      >{type}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Body *</label>
                  <textarea rows={5} value={builderData.bodyText} onChange={(e) => setBuilderData({ ...builderData, bodyText: e.target.value })}
                    placeholder="Hello {{1}}, your order {{2}} has been shipped! Track it here: {{3}}"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400 font-mono" />
                  <p className="text-[10px] text-gray-400 mt-1">Use {"{{1}}"}, {"{{2}}"}, etc. for variables. Max 1024 characters.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Footer (Optional)</label>
                  <input type="text" value={builderData.footerText} onChange={(e) => setBuilderData({ ...builderData, footerText: e.target.value })}
                    placeholder="e.g. Reply STOP to unsubscribe" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Buttons (Optional)</label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <select className="px-3 py-2 rounded-xl border border-gray-200 text-sm">
                        <option>Call to Action</option>
                        <option>Quick Reply</option>
                      </select>
                      <input type="text" placeholder="Button text" className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                      <input type="text" placeholder="URL or phone" className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm" />
                    </div>
                    <button className="flex items-center gap-1 text-xs font-medium" style={{ color: '#25D366' }}>
                      <Icons.Plus className="w-3 h-3" /> Add Button
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                    <Icons.Send className="w-4 h-4" /> Submit for Approval
                  </button>
                  <button className="px-5 py-2.5 rounded-xl text-gray-600 font-medium text-sm border border-gray-200 hover:bg-gray-50">
                    Save Draft
                  </button>
                </div>
              </div>

              {/* Preview */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Preview</h3>
                <div className="rounded-2xl overflow-hidden border border-gray-200" style={{ background: '#ECE5DD', maxWidth: '360px' }}>
                  <div className="px-4 py-3 flex items-center gap-3" style={{ background: '#075E54' }}>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <Icons.User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Your Business</p>
                      <p className="text-white/60 text-[10px]">Business Account</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="bg-white rounded-xl p-3 shadow-sm max-w-[280px]">
                      {builderData.bodyText ? (
                        <p className="text-sm text-gray-800 leading-relaxed">
                          {builderData.bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => `{{var${n}}}`)}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Type your message body to see preview...</p>
                      )}
                      {builderData.footerText && (
                        <p className="text-[10px] text-gray-400 mt-2">{builderData.footerText}</p>
                      )}
                      <p className="text-[9px] text-gray-400 text-right mt-1">10:30 AM</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Icons.Info className="w-4 h-4" /> Template Guidelines
                  </h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>- Body text is required, max 1024 characters</li>
                    <li>- Variables must be numbered sequentially: {"{{1}}"}, {"{{2}}"}, {"{{3}}"}</li>
                    <li>- Marketing templates require opt-out option</li>
                    <li>- Authentication templates support OTP button only</li>
                    <li>- Approval typically takes 24-48 hours</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWATemplates;
